import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiActivity, FiClock, FiX } from 'react-icons/fi';
import clsx from 'clsx';
import { playSchedulerNotificationSound } from '../utils/schedulerNotificationSound';

const TYPE_STYLES = {
  started: {
    statusColor: 'text-emerald-600',
    statusDot: 'bg-emerald-500',
    pulse: true,
  },
  waiting: {
    statusColor: 'text-blue-600',
    statusDot: 'bg-blue-500',
    pulse: false,
  },
  stopped: {
    statusColor: 'text-amber-600',
    statusDot: 'bg-amber-500',
    pulse: false,
  },
};

function NotificationIcon({ type }) {
  if (type === 'waiting') {
    return <FiClock className="text-dashboard-stats-primary h-3.5 w-3.5" aria-hidden />;
  }
  return <FiActivity className="text-dashboard-stats-primary h-3.5 w-3.5" aria-hidden />;
}

function SchedulerNotificationItem({ notification, onDismiss }) {
  const styles = TYPE_STYLES[notification.type] ?? TYPE_STYLES.stopped;

  useEffect(() => {
    playSchedulerNotificationSound(notification.type);
  }, [notification.type]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      onDismiss(notification.id);
    }, 6000);
    return () => clearTimeout(timerId);
  }, [notification.id, onDismiss]);

  return (
    <div
      role="status"
      className="pointer-events-auto w-full max-w-sm animate-scheduler-toast-in"
    >
      <div className="rounded-lg border border-gray-100 bg-white p-1.5 shadow-sm">
        <div className="rounded-xl bg-dashboard-stats-primary-light p-2.5">
          <dl className="flex h-full items-center">
            <dt className="flex-shrink-0 rounded-lg bg-dashboard-stats-primary-dark p-2">
              <NotificationIcon type={notification.type} />
            </dt>

            <dd className="ml-2 flex min-w-0 flex-1 items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[10px] font-medium text-dashboard-stats-info">
                  Scheduler
                </div>
                <div
                  className={clsx(
                    'flex items-center gap-1 truncate text-xs font-bold tabular-nums',
                    styles.statusColor
                  )}
                >
                  <span
                    className={clsx(
                      'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                      styles.statusDot,
                      styles.pulse && 'animate-pulse'
                    )}
                  />
                  {notification.title}
                </div>
                <p className="mt-0.5 text-[10px] leading-snug text-dashboard-stats-info">
                  {notification.message}
                </p>
              </div>

              <button
                type="button"
                onClick={() => onDismiss(notification.id)}
                className="flex-shrink-0 rounded-md p-1 text-main-light transition-colors hover:bg-main-base/60 hover:text-main-primary"
                aria-label="Dismiss notification"
              >
                <FiX className="h-3.5 w-3.5" />
              </button>
            </dd>
          </dl>
        </div>
      </div>
    </div>
  );
}

export default function SchedulerNotificationStack({ notifications, onDismiss }) {
  if (!notifications.length) {
    return null;
  }

  return createPortal(
    <div
      aria-live="polite"
      className="pointer-events-none fixed right-4 top-16 z-[5000] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 sm:right-5"
    >
      {notifications.map((notification) => (
        <SchedulerNotificationItem
          key={notification.id}
          notification={notification}
          onDismiss={onDismiss}
        />
      ))}
    </div>,
    document.body
  );
}
